import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Bot, 
  Heart, 
  AlertTriangle, 
  Pill, 
  Activity, 
  Utensils, 
  Smile, 
  Brain,
  ExternalLink,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Quote,
  Globe
} from 'lucide-react';

const AgentResponse = ({ message }) => {
  const [showResources, setShowResources] = useState(false);
  const [showCitations, setShowCitations] = useState(false);

  const getAgentIcon = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return <Heart className="w-4 h-4" />;
      case 'EmergencyAgent':
        return <AlertTriangle className="w-4 h-4" />;
      case 'MedicationAgent':
        return <Pill className="w-4 h-4" />;
      case 'VitalsMonitoringAgent':
        return <Activity className="w-4 h-4" />;
      case 'NutritionAgent':
        return <Utensils className="w-4 h-4" />;
      case 'MentalHealthAgent':
        return <Smile className="w-4 h-4" />;
      case 'HealthResearchAgent':
        return <Brain className="w-4 h-4" />;
      default:
        return <Bot className="w-4 h-4" />;
    }
  };

  const getAgentColor = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return 'from-blue-500 to-blue-600';
      case 'EmergencyAgent':
        return 'from-red-500 to-red-600';
      case 'MedicationAgent':
        return 'from-purple-500 to-purple-600';
      case 'VitalsMonitoringAgent':
        return 'from-green-500 to-green-600';
      case 'NutritionAgent':
        return 'from-orange-500 to-orange-600';
      case 'MentalHealthAgent':
        return 'from-pink-500 to-pink-600';
      case 'HealthResearchAgent':
        return 'from-indigo-500 to-indigo-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getAgentDisplayName = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return 'Sophira';
      case 'EmergencyAgent':
        return 'Emergency Specialist';
      case 'MedicationAgent':
        return 'Medication Specialist';
      case 'VitalsMonitoringAgent':
        return 'Vital Signs Specialist';
      case 'NutritionAgent':
        return 'Nutrition Specialist';
      case 'MentalHealthAgent':
        return 'Mental Health Specialist';
      case 'HealthResearchAgent':
        return 'Medical Researcher';
      default:
        return 'Health Assistant';
    }
  };

  // Procesar el texto para extraer citations
  const processCitations = (text) => {
    const citationRegex = /\[(\d+)\]/g;
    const citations = [];
    let match;
    
    while ((match = citationRegex.exec(text)) !== null) {
      citations.push(parseInt(match[1]));
    }
    
    return [...new Set(citations)].sort((a, b) => a - b);
  };

  // Generar recursos basados en el agente y contenido
  const generateResources = (agentName, content) => {
    const baseResources = {
      MainHealthAgent: [
        { title: "Mayo Clinic - Health Information", url: "https://www.mayoclinic.org/", type: "medical" },
        { title: "MedlinePlus", url: "https://medlineplus.gov/", type: "medical" },
        { title: "CDC - Centers for Disease Control", url: "https://www.cdc.gov/", type: "government" }
      ],
      NutritionAgent: [
        { title: "Nutrition.gov", url: "https://www.nutrition.gov/", type: "government" },
        { title: "USDA - MyPlate", url: "https://www.myplate.gov/", type: "government" },
        { title: "American Heart Association - Nutrition", url: "https://www.heart.org/en/healthy-living/healthy-eating", type: "organization" },
        { title: "Academy of Nutrition and Dietetics", url: "https://www.eatright.org/", type: "professional" }
      ],
      MedicationAgent: [
        { title: "FDA - Drug Information", url: "https://www.fda.gov/drugs", type: "government" },
        { title: "Drugs.com", url: "https://www.drugs.com/", type: "medical" },
        { title: "RxList", url: "https://www.rxlist.com/", type: "medical" }
      ],
      EmergencyAgent: [
        { title: "American Red Cross", url: "https://www.redcross.org/", type: "organization" },
        { title: "FEMA - Emergency Preparedness", url: "https://www.ready.gov/", type: "government" },
        { title: "911.gov", url: "https://www.911.gov/", type: "government" }
      ],
      VitalsMonitoringAgent: [
        { title: "American Heart Association", url: "https://www.heart.org/", type: "organization" },
        { title: "CDC - Blood Pressure", url: "https://www.cdc.gov/bloodpressure/", type: "government" },
        { title: "NIH - National Heart Institute", url: "https://www.nhlbi.nih.gov/", type: "government" }
      ],
      MentalHealthAgent: [
        { title: "SAMHSA - Mental Health", url: "https://www.samhsa.gov/", type: "government" },
        { title: "NAMI - National Alliance on Mental Illness", url: "https://www.nami.org/", type: "organization" },
        { title: "NIH - National Institute of Mental Health", url: "https://www.nimh.nih.gov/", type: "government" }
      ],
      HealthResearchAgent: [
        { title: "PubMed - Medical Database", url: "https://pubmed.ncbi.nlm.nih.gov/", type: "research" },
        { title: "NIH - National Institutes of Health", url: "https://www.nih.gov/", type: "government" },
        { title: "Cochrane Library", url: "https://www.cochranelibrary.com/", type: "research" }
      ]
    };

    return baseResources[agentName] || baseResources.MainHealthAgent;
  };

  const citations = processCitations(message.text);
  const resources = generateResources(message.agent, message.text);
  
  // Use sources from message if available, otherwise generate default resources
  const actualSources = message.sources && message.sources.length > 0 ? message.sources : [];
  const displayResources = actualSources.length > 0 ? actualSources : resources;

  // Componente personalizado para markdown
  const MarkdownComponents = {
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold text-gray-700 mb-2 mt-4">
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p className="text-gray-700 mb-3 leading-relaxed">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc pl-6 mb-4 space-y-1">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 mb-4 space-y-1">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-gray-700">
        {children}
      </li>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-800">
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em className="italic text-gray-600">
        {children}
      </em>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-400 pl-4 py-2 bg-blue-50 rounded-r-lg mb-4">
        <Quote className="w-4 h-4 text-blue-500 mb-2" />
        {children}
      </blockquote>
    ),
    code: ({ children, inline }) => (
      inline ? (
        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">
          {children}
        </code>
      ) : (
        <code className="block bg-gray-100 p-4 rounded-lg text-sm font-mono text-gray-800 mb-4 overflow-x-auto">
          {children}
        </code>
      )
    ),
    hr: () => <hr className="border-gray-300 my-6" />
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'government':
        return '🏛️';
      case 'medical':
        return '🏥';
      case 'organization':
        return '🏢';
      case 'professional':
        return '👨‍⚕️';
      case 'research':
        return '🔬';
      default:
        return '📚';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 rounded-2xl shadow-lg border border-gray-200/50 backdrop-blur-sm overflow-hidden"
    >
      {/* Agent Header */}
      <div className={`bg-gradient-to-r ${getAgentColor(message.agent)} px-4 py-3`}>
        <div className="flex items-center space-x-3">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
            {getAgentIcon(message.agent)}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white text-sm">
              {getAgentDisplayName(message.agent)}
            </h3>
            <p className="text-xs text-white/80">
              Powered by Perplexity Sonar • {message.model}
            </p>
          </div>
          <div className="text-xs text-white/70">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown 
            components={MarkdownComponents}
            remarkPlugins={[remarkGfm]}
          >
            {message.text}
          </ReactMarkdown>
        </div>

        {/* Citations Section */}
        {(citations.length > 0 || actualSources.length > 0) && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowCitations(!showCitations)}
              className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              <span>References ({Math.max(citations.length, actualSources.length)})</span>
              {showCitations ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            <AnimatePresence>
              {showCitations && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden mt-3"
                >
                  <div className="space-y-2">
                    {actualSources.length > 0 ? (
                      // Show actual sources from Perplexity API
                      actualSources.map((source, index) => (
                        <motion.a
                          key={index}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200 hover:border-blue-300 hover:shadow-md transition-all group"
                          whileHover={{ scale: 1.02 }}
                        >
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded-full text-xs font-bold flex-shrink-0 mt-0.5">
                            {source.id || index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-800 group-hover:text-blue-600 transition-colors">
                              {source.title}
                            </h4>
                            {source.snippet && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {source.snippet}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {source.domain || (source.url ? new URL(source.url).hostname : source.url)}
                            </p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0 mt-1" />
                        </motion.a>
                      ))
                    ) : (
                      // Fallback to citation numbers
                      citations.map((citation) => (
                        <div 
                          key={citation}
                          className="bg-gray-50 rounded-lg p-3 text-sm"
                        >
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white rounded-full text-xs font-bold mr-3">
                            {citation}
                          </span>
                          <span className="text-gray-700">
                            Source verified by Perplexity Sonar
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Resources Section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowResources(!showResources)}
            className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            <Globe className="w-4 h-4" />
            <span>Additional Resources ({displayResources.length})</span>
            {showResources ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          <AnimatePresence>
            {showResources && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden mt-3"
              >
                <div className="space-y-2">
                  {displayResources.map((resource, index) => (
                    <motion.a
                      key={index}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group"
                      whileHover={{ scale: 1.02 }}
                    >
                      <span className="text-lg">{getResourceIcon(resource.type)}</span>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-800 group-hover:text-blue-600 transition-colors">
                          {resource.title}
                        </h4>
                        <p className="text-xs text-gray-500 truncate">
                          {resource.url}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Agent Metadata */}
        {message.agentInfo && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                Processed by {message.agentInfo.agent}
              </span>
              {message.agentInfo.canEscalate && (
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                  Escalation available
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AgentResponse; 